/**
 *
 * CreateMessage
 *
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// layout components
import Preloader from "components/Preloader";
import ErrorBoundary from "engine/ErrorBoundary";

// antd component
import { ActionSheet, Toast } from "antd-mobile";
import { Form, Input, Button, message } from "antd";

// assets
import CustomIcons from "components/CustomIcons";
import { CloseOutlined, SendOutlined } from "@ant-design/icons";

// Communication components
import { AudioRecorder } from "components/Audio";
import TCFModal from "components/TCFModal";
import HiddenField from "components/Forms/Fields/HiddenField";

// utils
import api from "utils/api";
import { truncate } from "utils/helpers/helper.general";
// hooks
import { useRecorder } from "utils/hooks";
import _local from "utils/localStorageUtils";
// capacitor hekpers
import { appHaptics, appHelper } from "appCapacitor/helpers";

const CreateMessageMobile = ({
  disabled,
  loading,
  onSend,
  setReply,
  reply,
  messagesListRef,
  dataList,
  // default props
  className,
}) => {
  const childClassNames = classNames("create-message-mobile", className);
  const [t, i18n] = useTranslation();

  const localUser = _local.getLocalUser();
  const localActiveChild = _local.getLocalChild();

  const [discussionForm] = Form.useForm();
  const textAreaRef = useRef(null);
  const recorder = useRecorder();

  const messageContentValue = Form.useWatch(["content"], discussionForm);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);

  let aggrementTimeout = false;

  useEffect(() => {
    return () => {
      !!aggrementTimeout && clearTimeout(aggrementTimeout);
    };
  }, []);

  useEffect(() => {
    // INFO: changed this code `Capacitor.getPlatform() !== "ios"` to this one `!appHelper?.isIos`
    if (textAreaRef.current && !appHelper?.isIos) {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      if (
        textAreaRef.current.resizableTextArea.textArea.clientHeight <= 120 ||
        textAreaRef.current.resizableTextArea.textArea.scrollHeight <= 150
      ) {
        textAreaRef.current.resizableTextArea.textArea.style.height = "0px";
        const scrollHeight =
          textAreaRef.current.resizableTextArea.textArea.scrollHeight;

        // We then set the height directly, outside of the render loop
        // Trying to set this with state or a ref will product an incorrect value.
        textAreaRef.current.resizableTextArea.textArea.style.height =
          scrollHeight + 2 + "px";
      }
    }
  }, [textAreaRef.current, messageContentValue, recorder?.audio]);

  useEffect(() => {
    // INFO: changed this code `Capacitor.getPlatform() !== "ios"` to this one `appHelper?.isIos`
    if (!appHelper?.isIos) {
      if (messagesListRef.current && textAreaRef.current) {
        messagesListRef.current.style.height = `calc(100vh - ${textAreaRef
          .current.resizableTextArea.textArea.clientHeight +
          113 +
          (reply
            ? 35
            : 0)}px - env(safe-area-inset-top) - env(safe-area-inset-bottom))`;
      }
    }
  }, [
    messagesListRef,
    messageContentValue,
    textAreaRef,
    dataList,
    recorder?.audio,
    reply,
  ]);

  const handleFormFinish = values => {
    if (values?.content || recorder?.audio) {
      if (recorder?.audio) {
        setUploadLoading(true);
        const file = new File([recorder?.audio], `files-${uuidv4()}.wav`, {
          type: "audio/wav",
        });
        const form = new FormData();
        form.append("audio", file);

        let sentMsgObject = {
          student: localActiveChild?._id,
        };

        if (!!values?.visibility) {
          sentMsgObject.visibility = values.visibility;
        }

        api.discussions
          .uploadVoiceMessage(form)
          .then(data => {
            setUploadLoading(false);
            onSend({
              ...sentMsgObject,
              media: {
                type: "audio",
                url: data?.url,
              },
            });
            //setConfirmModalOpened(false);

            recorder?.deleteRecording();
          })
          .catch(err => {
            console.error(err);
            discussionForm.resetFields();
          });
      } else {
        const { visibility, ...restVal } = values;
        const sentMsgObject = {
          ...restVal,
          student: localActiveChild?._id,
        };
        if (visibility) {
          sentMsgObject.visibility = visibility;
        }
        onSend(sentMsgObject);
        discussionForm.resetFields();
        //setConfirmModalOpened(false);
      }
    } else {
      message.error(t("messages.mandatoryMessage"));
      setUploadLoading(false);
    }
  };

  const renderReplay = useMemo(() => {
    return (
      !!reply && (
        <div className="message-reply-mobile">
          <div className="message-reply-mobile--header">
            <CustomIcons
              type="reply-left"
              className="message-reply-mobile--header__icon"
            />
            <div className="message-reply-mobile--header__content">
              <span className="reply-name">
                {reply?.sender?.description?.firstname}{" "}
                {reply?.sender?.description?.lastname}
              </span>
              <div
                className="message-reply-mobile__text"
                dangerouslySetInnerHTML={{
                  __html: reply?.content
                    ? truncate(reply?.content, 50)
                    : t("messages.voiceMessage")
                }}
              />
            </div>
          </div>
          <CloseOutlined onClick={() => setReply(null)} />
        </div>
      )
    );
  }, [reply, setReply, i18n?.language]);

  const onSaveMessage = useCallback(() => {
    discussionForm.submit();
    appHaptics.hapticsImpactLight();
  }, []);

  // Modal
  {/* INFO: disable this feature for task SCHOOL-1101 */}
  /* const onSentMessageHandler = useCallback(() => {
    onSaveMessage();
    !loading && setConfirmModalOpened(false);
  }, [loading]);
  const onVisibleForAllHandler = useCallback(() => {
    discussionForm.setFieldValue(["visibility"], "all");
    onSentMessageHandler();
  }, [onSentMessageHandler]);
  const onVisibleForAdminsHandler = useCallback(() => {
    discussionForm.setFieldValue(["visibility"], "admin");
    onSentMessageHandler();
  }, [onSentMessageHandler]); */


  {/* INFO: disable this feature for task SCHOOL-1101 */}
  /* const renderAggrementModal = useCallback(() => {
    return (
      <TCFModal
        wrapClassName="create-message-modal"
        open={confirmModalOpened}
        setOpen={setConfirmModalOpened}
        title="Rendre ce message uniquement visible par l’administration scolaire et les enseignants"
        loading={loading}
        confirmLoading={loading}
        onCancel={onVisibleForAllHandler}
        onOk={onVisibleForAdminsHandler}
        cancelName="Non"
        submitName="Oui"
      >
        <></>
      </TCFModal>
    );
  }, [confirmModalOpened, loading]); */

  {/* INFO: disable this feature for task SCHOOL-1101 */}
  /* const renderAggrementSheet = useCallback(() => {
    return (
      <ActionSheet
        popupClassName="create-message-sheet"
        extra="Rendre ce message uniquement visible par l’administration scolaire et les enseignants"
        visible={confirmModalOpened}
        safeArea={true}
        actions={[
          {
            text: "Non",
            key: "all",
            // description: 'description',
            danger: false,
            bold: true,
            onClick: () => onVisibleForAllHandler(),
          },
          {
            text: "Oui",
            key: "admin",
            //  description: 'description',
            danger: false,
            bold: true,
            onClick: () => onVisibleForAdminsHandler(),
          },
        ]}
        onClose={() => setConfirmModalOpened(false)}
      />
    );
  }, [confirmModalOpened, loading]); */

  return (
    <div className={childClassNames}>
      <ErrorBoundary>
        {renderReplay}
        <Form
          form={discussionForm}
          name="new_message_form"
          className={classNames(
            `message-form-mobile`,
            disabled && "disabled",
            (loading || uploadLoading) && "loading",
          )}
          onFinish={handleFormFinish}
          //disabled={disabled}
        >
          <HiddenField data={{ name: "visibility" }} index={0} />

          {!recorder?.isRecording && !recorder?.audio && (
            <Form.Item name="content" noStyle>
              <Input.TextArea
                rows={2}
                placeholder={t("messages.yourMessage")}
                className="message-form-mobile--content"
                ref={textAreaRef}
                id="new_message_form_content"
              />
            </Form.Item>
          )}
          {(!messageContentValue ||
            (messageContentValue && messageContentValue.length === 0)) && (
            <AudioRecorder
              loading={loading || uploadLoading}
              recorder={recorder}
            />
          )}
          {(messageContentValue?.length > 0 || recorder?.audio) && (
            <Form.Item noStyle>
              <div
                className={classNames(
                  "message-form-mobile__submit",
                  (loading || uploadLoading) && "loading",
                )}
              >
                {(loading || uploadLoading) && <Preloader size="small" />}
                {!loading && !uploadLoading && (
                  <Button
                    className="message-form-mobile__submit-btn"
                    type="default"
                    htmlType="button"
                    onClick={() => {
                      onSaveMessage();
                      {/* INFO: disable this feature for task SCHOOL-1101 */}
                      /* if (localUser?.role == "teacher") {
                        if (
                          (!!reply && reply?.senderRole == "admin") ||
                          reply?.senderRole == "teacher"
                        ) {
                          onSaveMessage();
                        } else {
                          aggrementTimeout = setTimeout(
                            setConfirmModalOpened(true),
                            300,
                          );
                        }
                      } else {
                        aggrementTimeout = setTimeout(
                          setConfirmModalOpened(true),
                          300,
                        );
                      } */
                    }}
                  >
                    <SendOutlined />
                  </Button>
                )}
              </div>
            </Form.Item>
          )}

          {/* INFO: disable this feature for task SCHOOL-1101 */}
          {/* {renderAggrementModal()} */}
          {/* {renderAggrementSheet()} */}
        </Form>
      </ErrorBoundary>
    </div>
  );
};

CreateMessageMobile.defaultProps = {
  loading: false,
  disabled: false,
};
CreateMessageMobile.propTypes = {};

export default CreateMessageMobile;
